<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { useToast } from "@/composables/useToast";
import ToastItem from "./ToastItem.vue";

const { toasts, dismiss, dismissAll, pauseAll, resumeAll } = useToast();

/* The sign-in screens (and the other full-screen routes) have no navbar, but
   their own fixed language/theme controls and Back link at `top-5 z-50`, pushed
   down by the promo bar. See FULL-SCREEN ROUTES below. */
const store = useStore();
const isFullScreen = computed(() => store.state.isFullScreen);

// One card is dismissed with its own button; three is a pile worth one click.
const showClearAll = computed(() => toasts.value.length > 2);

/*
 * Escape dismisses the toast that currently holds focus rather than the whole
 * stack. Blowing away every message because you pressed Escape to close a
 * dropdown would throw away the error you had not read yet.
 */
const onEscape = (event) => {
  const card = event.target?.closest?.("[data-toast-id]");
  if (!card) return;
  dismiss(Number(card.dataset.toastId));
};
</script>

<template>
  <!--
    TOP-RIGHT, not the bottom-right the old library used.

    Crisp's chat launcher is fixed to the bottom-right corner (index.html loads
    client.crisp.chat), and its open panel is a ~350px column in that same
    corner. Bottom-right toasts sat underneath the launcher — the dismiss button
    of the lowest one was literally unclickable — and disappeared entirely
    behind an open chat, which is exactly when someone is most likely to be
    reporting the error a toast just raised.

    Bottom-LEFT was the other candidate and was rejected: the sidebar occupies
    that corner on desktop, and on mobile the bottom edge is where the browser
    chrome and the thumb live.

    `top-[4.5rem]` clears the fixed h-14 navbar.

    LAYERING. This host renders inside #app, and #app is `isolation: isolate`
    (css/_app.css), so no z-index set here can rise above anything teleported
    to <body> — ModalBox, the notification bell and the language list all
    paint above it whatever this number says. (The z-index of 10000 this used
    to carry claimed to beat ModalBox; it never could.) Within #app the number
    is chosen to sit
      - above page content, which tops out at the campaigns table's sticky
        `z-30` header, so a toast is never hidden under a page;
      - below the navbar (`z-[32]`), because the account menu and the phone
        search sheet hang below the bar INSIDE its stacking context: at 10000
        a toast covered the open account menu and ate the clicks meant for it
        (ToastItem dismisses on any click);
      - below the drawer backdrop (`z-[33]`, App.vue) and the drawer (`z-40`),
        which dim and cover everything while the sidebar is open on a phone.
    Header popovers always win; toasts still beat everything on the page.

    FULL-SCREEN ROUTES (sign-in, register, password reset, error). No navbar,
    so none of the above applies, but those pages pin their language/theme
    controls and Back link at `top-5` (plus the promo bar's height) at
    `z-50`. With the promo bar showing they sat right over the stack: the
    theme toggle covered a toast's Dismiss button, so tapping it switched the
    theme instead — and the sign-in failure notice appears exactly there.
    So on those routes the stack starts below that row
    (`.toast-host-below-controls`) and is lifted over it (`z-[60]`) for
    anything that still meets it.

    The container is `pointer-events-none` so this full-width strip does not eat
    clicks meant for the page; only the cards themselves take pointer events.
    The hover handlers still fire: `mouseenter` is dispatched to every ancestor
    of the hit target, and `pointer-events: none` only stops an element being
    the target, not receiving an event on its descendant's behalf.
  -->
  <div
    class="fixed left-2 right-2 sm:left-auto sm:right-4 sm:w-[23rem] flex flex-col items-stretch gap-2 pointer-events-none"
    :class="isFullScreen ? 'toast-host-below-controls z-[60]' : 'z-[31] top-[4.5rem]'"
    @mouseenter="pauseAll"
    @mouseleave="resumeAll"
    @focusin="pauseAll"
    @focusout="resumeAll"
    @keydown.esc="onEscape"
  >
    <button
      v-if="showClearAll"
      type="button"
      class="pointer-events-auto self-end inline-flex items-center h-8 px-3 rounded-lg text-xs font-semibold shadow-card bg-white text-gray-600 hover:text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-white dark:border-gray-600"
      @click="dismissAll"
    >
      Clear all
    </button>

    <transition-group name="toast-stack" tag="div" class="flex flex-col items-stretch gap-2">
      <toast-item
        v-for="toast in toasts"
        :key="toast.id"
        :toast="toast"
        @dismiss="dismiss"
      />
    </transition-group>
  </div>
</template>

<style>
/*
 * Not `scoped`. Vue applies transition classes to the child component's ROOT
 * element, and relying on the parent's scope id reaching that root is a subtle
 * coupling that breaks the moment ToastItem grows a second root node. The class
 * names are prefixed instead, which is the same protection without the trap.
 */
.toast-stack-enter-active {
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.3, 1), opacity 220ms ease-out;
}

.toast-stack-leave-active {
  transition: transform 160ms ease-in, opacity 160ms ease-in;
}

/* Slides in from the edge it is anchored to, so it reads as arriving rather
   than as the page having shifted. */
.toast-stack-enter-from,
.toast-stack-leave-to {
  opacity: 0;
  transform: translateX(1.5rem);
}

.toast-stack-move {
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.3, 1);
}

/* Full-screen routes: under the pinned controls row, which sits at
   `.below-promo` (1.25rem + the promo bar's height, css/_app.css) and is at
   most 2.75rem tall (the 44px touch floor) — so 4.5rem plus the promo height
   leaves the same 0.5rem gap the navbar layout has. A plain rule rather than
   an arbitrary-value top utility, for the reason given at `.below-promo`.
   (Don't spell such a class out in a comment: Tailwind's JIT scans comments
   too, and emitted an invalid `top: calc(...)` rule for the example.) */
.toast-host-below-controls {
  top: calc(4.5rem + var(--promo-height, 0px));
}

@media (prefers-reduced-motion: reduce) {
  .toast-stack-enter-active,
  .toast-stack-leave-active,
  .toast-stack-move {
    transition: opacity 120ms linear;
  }

  .toast-stack-enter-from,
  .toast-stack-leave-to {
    transform: none;
  }
}
</style>
